/** @format */
// line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
// Determine the intersection point of two line segments
// Return FALSE if the lines don't intersect
const calcIntersection = (x1, y1, x2, y2, x3, y3, x4, y4) => {
    // Check if none of the lines are of length 0
    if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
        return false;
    }

    const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

    // Lines are parallel
    if (denominator === 0) {
        return false;
    }

    let ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
    let ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

    // is the intersection along the segments
    if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
        return false;
    }

    // Return a object with the x and y coordinates of the intersection
    let x = x1 + ua * (x2 - x1);
    let y = y1 + ua * (y2 - y1);

    return {x, y};
};

const kissOffset = 0.08;
const kissSpacing = 0.25;

const getGlueKissLines = ({depth, topBoxOffsetY, glueFlapWidth}) => {
    const kissLineWidth = glueFlapWidth - kissOffset * 2;
    const slope = 0.13 / glueFlapWidth;
    const topKissLine = topBoxOffsetY + 0.13 + kissSpacing;

    const kissLineCount = Math.floor((depth - kissSpacing * 2) / kissSpacing);
    const kissLines = [];
    for (let i = 0; i < kissLineCount; i++) {
        if (i === kissLineCount - 1) {
            // Last line is shorter
            const intersection = calcIntersection(
                0.5,
                topBoxOffsetY + depth - kissOffset,
                -0.5,
                topBoxOffsetY + depth - kissOffset - 1,
                kissOffset,
                topKissLine + kissSpacing * i,
                kissOffset + kissLineWidth,
                topKissLine + kissSpacing * i + slope * kissLineWidth * -1
            );

            const lastKissLineWidth = glueFlapWidth - kissOffset - intersection.x;
            kissLines.push([
                `M ${intersection.x} ${intersection.y}`,
                `l ${lastKissLineWidth} ${slope * lastKissLineWidth * -1}`
            ]);
        } else {
            kissLines.push([
                `M ${kissOffset} ${topKissLine + kissSpacing * i}`,
                `l ${kissLineWidth} ${slope * kissLineWidth * -1}`
            ]);
        }
    }
    return kissLines;
};

const getGlueFlapKnockout = ({offsetLeft, glueFlapWidth, topBoxOffsetY, depth}) => {
    const width = glueFlapWidth - 0.125;
    const trim = 0.125; // Trim to help with visibility issues
    const top = topBoxOffsetY;
    const bleed = 0.125;
    const height = depth - trim - bleed / 2;
    return [
        {x: offsetLeft, y: top},
        {x: offsetLeft + width, y: top},
        {x: offsetLeft + width, y: top + height},
        {x: offsetLeft, y: top + height - 0.5 + bleed},
        {x: offsetLeft, y: top + 0.5 - bleed}
    ];
};

/**
 * Knockout for cartons for use with primer ink
 */
const getGlueFlapKnockoutDuplex = ({offsetLeft, glueFlapWidth, topBoxOffsetY, depth}) => {
    const width = glueFlapWidth - 0.125;
    const trim = 0.125; // Trim to help with visibility issues
    const top = topBoxOffsetY + trim;
    const bleed = 0.125;
    const height = depth - trim * 2;
    return [
        {x: offsetLeft, y: top + 0.5 - bleed},
        {x: offsetLeft + width, y: top},
        {x: offsetLeft + width, y: top + height},
        {x: offsetLeft, y: top + height - 0.5 + bleed},
        {x: offsetLeft, y: top + 0.5 - bleed}
    ];
};

const getTuckFlapKnockout = ({
    offsetLeft,
    topBoxOffsetY,
    width,
    length,
    depth,
    bottomTuckHeight
}) => {
    // This is the flap of the closure panel rotated 90 degrees and shrunk by .063in
    // Spacing to shrink the knockout
    const glueSpaceOffset = 0.063;
    const offsetY = width * 0.1 + glueSpaceOffset;
    // These come from the closureFlap This is the left diagonal line's slope
    const leftDiagonalX = width / 2 + width * 0.318 - width;
    const leftDiagonalY = -width / 2;
    // Use the slope from the closure flap to determine the offset
    const offsetX = (leftDiagonalX * offsetY) / leftDiagonalY;
    const topLeftY = topBoxOffsetY + depth + offsetY;
    const topLeftX = offsetLeft + offsetX;
    const topRightY = topLeftY + 0.04 * width * -1;
    const knockoutWidth = width / 2 + bottomTuckHeight - 0.14;
    return [
        {x: topLeftX, y: topLeftY}, // Top left corner
        {x: offsetLeft + knockoutWidth, y: topRightY}, // Slight slop up
        {x: offsetLeft + knockoutWidth, y: topRightY}, // Flat top
        {
            x: offsetLeft + knockoutWidth,
            y: topBoxOffsetY + depth + width / 2
        }, // Right vertical line
        {x: offsetLeft + width - width / 2, y: topBoxOffsetY + depth + width / 2}, // Right diagonal line
        {x: -width * 0.318, y: 0, relative: true} // Flat bottom
    ];
};

const getBottomTuckKnockoutDuplex = ({
    offsetLeft,
    topBoxOffsetY,
    depth,
    width,
    length,
    bottomTuckHeight,
    bleed
}) => {
    var trim = 0.0625; // trim to remove some small visible parts
    var topY = topBoxOffsetY + depth + trim;
    var bottomY = topBoxOffsetY + depth + width / 2 + bottomTuckHeight + bleed;
    var rightX = offsetLeft;
    var leftX = rightX - width / 2;
    return [
        {x: rightX, y: topY},
        {x: leftX, y: topY + width / 2},
        {x: leftX, y: bottomY},
        {x: rightX, y: bottomY},
        {x: rightX, y: topY}
    ];
};

// All I want is closure.
const getClosureKissLines = ({topBoxOffsetY, leftOffset, width, length, depth}) => {
    const lineWidth = width / 2 + 0.375 - 0.125;
    const leftAnchor = leftOffset;
    const rightAnchor = leftOffset + width - kissOffset * 2;
    const rightLineAnchor = leftOffset + lineWidth;
    // How many can we fit?
    const lineCount = Math.floor(lineWidth / kissSpacing);

    const line1X1 = leftAnchor;
    const line1Y1 = topBoxOffsetY + depth;
    const line2X1 = rightAnchor;
    const line2Y1 = topBoxOffsetY + depth;

    const kissLines = [];
    const topOffset = topBoxOffsetY + depth + kissOffset;
    for (let i = 0; i < lineCount; i++) {
        const x = rightLineAnchor - kissSpacing * i;
        const intersection1 = calcIntersection(
            line1X1,
            line1Y1,
            line1X1 + 5,
            line1Y1 + 5,
            x,
            topOffset,
            x,
            topOffset + 5
        );

        const intersection2 = calcIntersection(
            line2X1,
            line2Y1,
            line2X1 - 5,
            line2Y1 + 5,
            x,
            topOffset,
            x,
            topOffset + 5
        );

        // If over halfway, use the right light, otherwise the left line intersection
        const y =
            intersection1.x > leftAnchor + width / 2 - kissOffset * 2
                ? intersection2.y
                : intersection1.y;

        const lineHeight = y - topOffset;
        if (lineHeight > 0.1) {
            kissLines.push([`M ${x} ${topOffset}`, `L ${x} ${y}`]);
        }
    }

    return kissLines;
};

export {
    getGlueKissLines,
    getClosureKissLines,
    calcIntersection,
    getGlueFlapKnockout,
    getTuckFlapKnockout,
    getBottomTuckKnockoutDuplex,
    getGlueFlapKnockoutDuplex
};
